import React, { useState, useEffect, Fragment } from 'react';
import isEmpty from 'lodash.isempty';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useHundredBricksAPI from '@lib/hundredBricksAPI/useHundredBricksAPI';
import PasswordUnlockForm from '@components/PasswordUnlockForm';
import { Button, InvestorLocationModal, OneTimePassword } from '@components';
import { Paragraph, Subtitle } from '@components/Styled';
import { renderSomethingWentWrong, renderSuccessToast } from '@utils';
import { InvestorIsNotLocked } from '@lib/hundredBricksAPI/authentication';
import { useAuth } from '@lib/authentication';
import { useErrorReporter } from '@lib/errorReporter';
import { useForm, useGeolocation, useRequest } from '@hooks';
import { LocationBlockedModal } from '@views/Login/components';

import { UnlockLayout, ErrorMessage, VPNErrorMessage } from './InactiveAccountUnlock.style';

const unlockSchema = {
  password: {
    required: true
  }
};

const InactiveAccountUnlock = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const form = useForm({ schema: unlockSchema });
  const api = useHundredBricksAPI();
  const errorReporter = useErrorReporter();
  const { getCurrentPosition, getGeolocationStatus } = useGeolocation();
  const [verificationCode, setVerificationCode] = useState();
  const [showVPNMessage, setShowVPNMessage] = useState(false);
  const {
    error,
    isLoading,
    token,
    unlockInactiveAccount
  } = useAuth();

  const [
    unlockCodeResponse,
    isSendingUnlockCode,
    unlockCodeError,
    sendUnlockCode
  ] = useRequest(api.authentication.sendInactiveAccountToken);

  const [displayLocationLockedModal, setDisplayLocationLockedModal] = useState(false);
  const [showInvestorLocationModal, setShowInvestorLocationModal] = useState(false);
  const [isApprovingLocation, setIsApprovingLocation] = useState(false);

  const handleSendEmailUnlockCode = () => {
    sendUnlockCode(searchParams.get('investorId'), 'email');
    form.removeError('code');
  };

  const handleUnlockInvestor = async () => {
    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      unlockInactiveAccount(
        {
          tokenCode: verificationCode,
          investorId: searchParams.get('investorId'),
          password: form.values.password,
          latitude,
          longitude
        }
      );
    }, () => {
      setIsApprovingLocation(false);
      setDisplayLocationLockedModal(true);
    });
  };

  const handleChangePassword = oneTimeCode => {
    setVerificationCode(oneTimeCode);

    form.removeError('code');
  };

  const acceptShareLocation = () => {
    setShowInvestorLocationModal(false);
    handleUnlockInvestor();
  };

  const cancelShareLocation = () => {
    setShowInvestorLocationModal(false);
    setIsApprovingLocation(false);
  };

  const onSubmit = async () => {
    setShowVPNMessage(false);
    setIsApprovingLocation(true);

    const geolocationStatus = await getGeolocationStatus();

    if (geolocationStatus === 'prompt') {
      setShowInvestorLocationModal(true);
    } else if (geolocationStatus === 'denied') {
      setDisplayLocationLockedModal(true);
      setIsApprovingLocation(false);
    } else if (geolocationStatus === 'granted') {
      handleUnlockInvestor();
    }
  };

  const handleErrors = apiError => {
    switch (apiError.code) {
      case 'CANT_RESEND_TOKEN':
        form.addError({
          field: 'code',
          message: 'Ocurrió un problema al enviar el código de verificación.'
        });
        break;
      case 'INCORRECT_PASSWORD':
        form.addError({
          field: 'password',
          message: 'La contraseña es inválida'
        });
        break;
      case 'INVALID_TOKEN':
        form.addError({
          field: 'code',
          message: 'El código ha expirado'
        });
        break;
      case 'INVALID_CODE':
        form.addError({
          field: 'code',
          message: 'El código es inválido'
        });
        break;
      case 'INVALID_LATITUDE':
        form.addError({
          field: 'password',
          message: 'La latitud de tu ubicación es inválida'
        });
        break;
      case 'INVALID_LONGITUDE':
        form.addError({
          field: 'password',
          message: 'La longitud de tu ubicación es inválida'
        });
        break;
      case 'INVALID_USER_CONNECTION':
        setShowVPNMessage(true);
        break;
      default:
        renderSomethingWentWrong();
        errorReporter.critical(apiError);
        break;
    }
  };

  useEffect(() => {
    if (unlockCodeResponse && !unlockCodeError) {
      renderSuccessToast('Se ha enviado el código exitosamente.');
    } else if (unlockCodeError && !(unlockCodeError instanceof InvestorIsNotLocked)) {
      handleErrors(unlockCodeError.cause);
    }
  }, [unlockCodeResponse, unlockCodeError]);

  useEffect(() => {
    if (token && !error) {
      navigate('/marketplace');
    } else if (error) {
      handleErrors(error.cause);
      setIsApprovingLocation(false);
    }
  }, [token, error]);

  const hasURLError = (
    error?.code === 'INVALID_ID' || error?.code === 'INVESTOR_NOT_FOUND'
  );

  const isSubmitting = isLoading || isApprovingLocation;

  return (
    <UnlockLayout>
      <Subtitle>Cuenta inactiva</Subtitle>

      {
        !(unlockCodeError && unlockCodeError instanceof InvestorIsNotLocked) && (
          <Fragment>
            <div>
              <Paragraph>
                Esta cuenta fue temporalmente bloqueada por
                inactividad. ¿Quieres desbloquear la cuenta?

                <br />

                <span>
                  Envía el código de seguridad a tu correo para desbloquearla.
                </span>
              </Paragraph>

              <Button
                color="secondary"
                isLoading={isSendingUnlockCode}
                disabled={!isEmpty(unlockCodeResponse)}
                onClick={handleSendEmailUnlockCode}
              >
                Enviar código
              </Button>
            </div>

            <OneTimePassword
              error={{ message: form.errors.code }}
              resendOptions={['email']}
              onChange={handleChangePassword}
              onSendCode={handleSendEmailUnlockCode}
            />

            <PasswordUnlockForm
              onSubmit={onSubmit}
              formProps={form}
              hasURLError={hasURLError}
              isSubmitting={isSubmitting}
              buttonText="Ir a iniciar sesión"
            />

            {Boolean(showVPNMessage) && (
              <VPNErrorMessage>
                Tenemos un inconveniente al tratar de iniciar tu sesión.
                ¿Tienes activa una VPN o un proxy?
                Desactívala y vuelve a intentarlo.
              </VPNErrorMessage>
            )}
          </Fragment>
        )
      }

      {hasURLError && (
        <ErrorMessage>
          <Paragraph>Al parecer algo cambió en la URL</Paragraph>
          <Paragraph>Abre de nuevo el enlace desde tu correo</Paragraph>
        </ErrorMessage>
      )}

      {
        (unlockCodeError && unlockCodeError instanceof InvestorIsNotLocked) && (
          <Fragment>
            <Paragraph>
              Tu cuenta no se encuentra bloqueada. Por favor, inicia sesión para continuar.
            </Paragraph>

            <Button
              type="button"
              onClick={() => { navigate('/login'); }}
            >
              Ir a iniciar sesion
            </Button>
          </Fragment>
        )
      }

      <InvestorLocationModal
        isOpen={showInvestorLocationModal}
        onConfirm={acceptShareLocation}
        onCancel={cancelShareLocation}
      />

      <LocationBlockedModal
        isOpen={displayLocationLockedModal}
        onConfirm={() => setDisplayLocationLockedModal(false)}
      />
    </UnlockLayout>
  );
};

export default InactiveAccountUnlock;
